import Backbone from "backbone";
import * as _ from "underscore";

import compositeViewMixin from "compositeViewMixin";
import * as mustcache from "mustcache";
import PublicLibraryListView from "views/PublicLibraryListView";

// Contains a LibraryListView child and provides pagination, fetching necessary data lazily from
// server

// Add an interface for the data object
interface PaginatedLibraryListViewData {
  from: number;
  to?: number;
  hasTexts?: boolean;
  prevButton?: boolean;
  nextButton?: boolean;
  uploadMessage?: boolean;
  searchQuery?: string;
}

const PaginatedLibraryListView = Backbone.View.extend({
  /**
   * @param {object} options.query
   */
  initialize: function (options) {
    _.extend(this, compositeViewMixin);
    this.options = options || {};

    this.models = options.models;
    this.searchQuery = options.searchQuery;

    // view state
    this.state = new Backbone.Model({
      page: 0,
      pageLimit: 20,
    });

    this.listenTo(this.searchQuery, "change", () => {
      console.log("serach query changed");
      this.state.set("page", 0);
      this.render();
    });

    this.listenTo(this.state, "change", this.render.bind(this));

    this.listenTo(
      this.models.user,
      "change:publicSortBooksBy change:publicBooksCategories change:publicBooksFilter",
      () => {
        this.state.set("page", 0);
        this.render();
      }
    );
    this.render();
  },
  fetch: function (done) {
    const query = _.extend(
      {
        language: this.models.user.get("currentLearningLanguage"),
        firstLanguage: this.models.user.get("firstLanguage"),
        sortBy: this.models.user.get("publicSortBooksBy"),
        categories: this.models.user.get("publicBooksCategories").join("|"),
        filter: this.models.user.get("publicBooksFilter"),
        limit: this.state.get("pageLimit") + 1, // fetch one extra item so we know if there's another page available
        skip: this.state.get("page") * this.state.get("pageLimit"),
        search: this.searchQuery.get("query"),
      },
      this.options.query
    );

    // Setting HTML here feels hacky, ideally would be done in render()
    this.$el.html("<p>Fetching...</p>");

    this.collection.fetch({
      reset: true,
      data: query,
      success: () => {
        if (done) {
          done();
        }
      },
    });
  },
  render: _.debounce(function () {
    this.fetch(this._render.bind(this));
  }, 0),
  _render: function () {
    const data: PaginatedLibraryListViewData = {
      from: this.state.get("page") * this.state.get("pageLimit") + 1,
    };
    data.to =
      this.state.get("page") * this.state.get("pageLimit") +
      Math.min(this.state.get("pageLimit"), this.collection.length);

    if (data.to >= data.from) {
      data.hasTexts = true;
      if (data.from > 1) {
        data.prevButton = true;
      }
      if (this.collection.length > this.state.get("pageLimit")) {
        data.nextButton = true;
      }
    } else {
      data.uploadMessage = true;
    }

    data.searchQuery =
      this.searchQuery.get("query") !== ""
        ? this.searchQuery.get("query")
        : undefined;

    /* TODO: re-introduce but only when there are no books AND no filters set
      if (data.to < data.from && this.state.get('page') === 0) {
        this.trigger('noBooks');
        this.options.rendered();
        return;
      }
      */

    mustcache.render("paginatedLibraryListView", data, (html) => {
      this.$el.html(html);
      this.setSubviews({
        libraryListView: new PublicLibraryListView(
          _.extend(this.options, {
            el: this.$('[data-subview="libraryList"]'),
            collection: new Backbone.Collection(
              this.collection.first(this.state.get("pageLimit"))
            ),
          })
        ),
      });
      this.delegateEvents(this.events);
    });
  },
  events: {
    "click .nextButton": "nextPage",
    "click .prevButton": "prevPage",
  },
  nextPage: function () {
    this.state.set("page", this.state.get("page") + 1);
  },
  prevPage: function () {
    this.state.set("page", this.state.get("page") - 1);
  },
});

export default PaginatedLibraryListView;
